<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    header="Edit Participants"
    :style="{ width: '28rem' }"
  >
    <!-- Subtitle -->
    <p class="text-navy-400 mb-5 text-xs">From {{ agentName }}</p>

    <!-- Loading state -->
    <div v-if="isFetching" class="flex items-center justify-center gap-3 py-12">
      <i class="pi pi-spin pi-spinner text-navy-400 text-lg" />
      <span class="text-navy-500 text-sm">
        Fetching contacts from {{ agentName }}...
      </span>
    </div>

    <template v-else>
      <!-- Contact list -->
      <div class="space-y-2">
        <label
          v-for="party in parties"
          :key="party.externalId"
          :class="[
            'flex cursor-pointer items-center gap-3.5 rounded-lg border-[1.5px] p-3.5 transition-all',
            isSelected(party.externalId)
              ? 'border-green-200 bg-green-50'
              : 'border-neutral-200 bg-white',
          ]"
        >
          <input
            type="checkbox"
            :checked="isSelected(party.externalId)"
            class="sr-only"
            @change="toggle(party.externalId)"
          />
          <!-- Checkbox indicator -->
          <div
            :class="[
              'flex h-5 w-5 shrink-0 items-center justify-center rounded border-[1.5px] transition-all',
              isSelected(party.externalId)
                ? 'border-green-500 bg-green-500'
                : 'border-neutral-300 bg-white',
            ]"
          >
            <svg
              v-if="isSelected(party.externalId)"
              class="h-3 w-3 text-white"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 12 12"
            >
              <path d="M2.5 6l2.5 2.5 4.5-5" />
            </svg>
          </div>

          <!-- Party info -->
          <div class="min-w-0 flex-1">
            <p class="text-navy-800 text-sm font-semibold">
              {{ party.name }}
            </p>
            <p
              v-if="party.contact"
              class="text-navy-400 mt-0.5 truncate text-xs"
            >
              {{ party.contact }}
            </p>
            <!-- Name change indicator -->
            <div
              v-if="
                nameChangeFor(party.externalId) && isSelected(party.externalId)
              "
              class="mt-1.5 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1"
              @click.prevent.stop
            >
              <input
                type="checkbox"
                :checked="acceptedNameUpdates.has(party.externalId)"
                class="h-3.5 w-3.5 shrink-0 cursor-pointer accent-amber-500"
                @change="toggleNameUpdate(party.externalId)"
              />
              <span class="text-navy-600 text-xs">
                <span class="text-navy-400"
                  >"{{ nameChangeFor(party.externalId)!.oldName }}"</span
                >
                <span class="text-navy-400 mx-0.5">&rarr;</span>
                <span class="font-medium"
                  >"{{ nameChangeFor(party.externalId)!.newName }}"</span
                >
              </span>
            </div>
          </div>

          <!-- Role badge -->
          <StatusBadge
            :variant="roleBadge(party.role).variant"
            :label="roleBadge(party.role).label"
          />
        </label>
      </div>

      <!-- Note -->
      <p class="text-navy-400 mt-4 text-xs">
        Showing contacts from {{ agentName }}. Manually added participants are
        managed separately.
      </p>

      <!-- Refresh hint -->
      <div
        class="mt-4 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
      >
        <i class="pi pi-info-circle text-blue-500" />
        <div class="flex-1">
          <p class="text-navy-700 text-sm font-medium">Not seeing a contact?</p>
          <p class="text-navy-500 mt-0.5 text-xs">
            Contacts may have been updated in {{ agentName }} since this order
            was created.
          </p>
        </div>
        <button
          class="cursor-pointer rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50"
          @click="fetchParties"
        >
          Refresh
        </button>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between">
        <button
          class="text-navy-500 hover:text-navy-700 cursor-pointer text-sm transition-colors"
          @click="visible = false"
        >
          Cancel
        </button>
        <AppButton :loading="isSaving" :disabled="!hasChanges" @click="save">
          {{ saveLabel }}
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref, watch } from "vue"

import { useParticipantMutations } from "../../composables/useParticipantMutations"
import type {
  FetchedOrderData,
  FetchedOrderParty,
  OrderType,
} from "../../pages/Order/types"
import AppButton from "../ui/AppButton.vue"
import StatusBadge from "../ui/StatusBadge.vue"

const roleMap: Record<
  string,
  { variant: "blue" | "purple" | "info" | "neutral"; label: string }
> = {
  Buyer: { variant: "blue", label: "Buyer" },
  Seller: { variant: "purple", label: "Seller" },
  SettlementAgent: { variant: "info", label: "Settlement Agent" },
  ListingAgentBroker: { variant: "neutral", label: "Listing Agent" },
  SellingAgentBroker: { variant: "neutral", label: "Selling Agent" },
  Lender: { variant: "info", label: "Lender" },
}

const roleBadge = (role: string) =>
  roleMap[role] ?? { variant: "neutral" as const, label: role }

const props = defineProps<{
  order: OrderType
}>()

const visible = defineModel<boolean>("visible", { required: true })
const emit = defineEmits<{ success: [] }>()

const toast = useToast()
const confirm = useConfirm()
const {
  isFetching,
  fetchError,
  refreshedSnapshot,
  isSaving,
  cleanup,
  fetchParties: dispatchFetchParties,
  performSave: executeSave,
} = useParticipantMutations(toast)

const participantNodes = computed(() =>
  (props.order.participants.nodes ?? []).filter(
    (p): p is NonNullable<typeof p> => p != null,
  ),
)

const agentName = computed(
  () => props.order.importAgentRegistration?.name ?? "source system",
)

interface PartyRow {
  externalId: string
  name: string
  role: string
  contact: string | null
}

// Use refreshed snapshot if available, otherwise fall back to order's cached snapshot
const activeSnapshot = computed<FetchedOrderData | null>(
  () =>
    refreshedSnapshot.value ??
    (props.order.rawSnapshot as FetchedOrderData | null) ??
    null,
)

const parties = computed<PartyRow[]>(() => {
  const snapshot = activeSnapshot.value
  if (!snapshot?.Parties) return []

  return snapshot.Parties.map((p: FetchedOrderParty) => ({
    externalId: `${p.Role}-${p.Index}`,
    name: p.FullName,
    role: p.Role,
    contact: [p.Email, p.Phone].filter(Boolean).join(" \u00B7 ") || null,
  }))
})

// ─── Fetch + Poll (refresh from agent) ──────────────────────────

watch(visible, (isOpen) => {
  if (isOpen) {
    refreshedSnapshot.value = null
    fetchError.value = undefined
    resetSelection()
  } else {
    cleanup()
  }
})

const fetchParties = () => {
  dispatchFetchParties(
    props.order.importAgentRegistration!.id,
    props.order.externalFileNumber!,
    agentName.value,
    resetSelection,
  )
}

// ─── Name change detection ───────────────────────────────────────

interface NameChange {
  externalId: string
  oldName: string
  newName: string
}

const nameChanges = computed<NameChange[]>(() => {
  const changes: NameChange[] = []
  for (const party of parties.value) {
    const participant = participantNodes.value.find(
      (p) => p.externalId === party.externalId,
    )
    if (participant && participant.name !== party.name) {
      changes.push({
        externalId: party.externalId,
        oldName: participant.name,
        newName: party.name,
      })
    }
  }
  return changes
})

const acceptedNameUpdates = ref<Set<string>>(new Set())

const nameChangeFor = (externalId: string) =>
  nameChanges.value.find((c) => c.externalId === externalId)

const toggleNameUpdate = (externalId: string) => {
  const next = new Set(acceptedNameUpdates.value)
  if (next.has(externalId)) {
    next.delete(externalId)
  } else {
    next.add(externalId)
  }
  acceptedNameUpdates.value = next
}

// Initialize accepted name updates when name changes are detected
watch(nameChanges, (changes) => {
  const ids = new Set<string>()
  for (const c of changes) {
    ids.add(c.externalId)
  }
  acceptedNameUpdates.value = ids
})

// ─── Selection state ────────────────────────────────────────────

const selectedIds = ref<Set<string>>(new Set())
const initialIds = ref<Set<string>>(new Set())

const resetSelection = () => {
  const current = new Set(
    participantNodes.value
      .filter((p) => p.source === "imported" && p.externalId)
      .map((p) => p.externalId!),
  )
  selectedIds.value = new Set(current)
  initialIds.value = new Set(current)
}

const isSelected = (id: string) => selectedIds.value.has(id)

const toggle = (id: string) => {
  const next = new Set(selectedIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  selectedIds.value = next
}

// ─── Diff summary ───────────────────────────────────────────────

const addCount = computed(() => {
  let count = 0
  for (const id of selectedIds.value) {
    if (!initialIds.value.has(id)) count++
  }
  return count
})

const removeCount = computed(() => {
  let count = 0
  for (const id of initialIds.value) {
    if (!selectedIds.value.has(id)) count++
  }
  return count
})

const activeNameUpdateCount = computed(() => {
  let count = 0
  for (const c of nameChanges.value) {
    if (
      acceptedNameUpdates.value.has(c.externalId) &&
      selectedIds.value.has(c.externalId)
    ) {
      count++
    }
  }
  return count
})

const hasChanges = computed(
  () =>
    addCount.value > 0 ||
    removeCount.value > 0 ||
    activeNameUpdateCount.value > 0 ||
    refreshedSnapshot.value !== null,
)

const saveLabel = computed(() => {
  const parts: string[] = []
  if (addCount.value > 0) parts.push(`Add ${addCount.value}`)
  if (removeCount.value > 0) parts.push(`Remove ${removeCount.value}`)
  if (activeNameUpdateCount.value > 0)
    parts.push(
      `Update ${activeNameUpdateCount.value} ${activeNameUpdateCount.value === 1 ? "name" : "names"}`,
    )
  if (parts.length > 0) return parts.join(", ")
  if (refreshedSnapshot.value) return "Save"
  return "No changes"
})

// ─── Save ───────────────────────────────────────────────────────

const removingHasVerifications = computed(() => {
  for (const id of initialIds.value) {
    if (selectedIds.value.has(id)) continue
    const participant = participantNodes.value.find((p) => p.externalId === id)
    if (
      participant?.participantVerifications &&
      participant.participantVerifications.length > 0
    ) {
      return true
    }
  }
  return false
})

const save = () => {
  if (removingHasVerifications.value) {
    confirm.require({
      message:
        "One or more participants being removed have submitted identity verification. Removing them will delete their verification data. Continue?",
      header: "Confirm Removal",
      icon: "pi pi-exclamation-triangle",
      acceptClass: "p-button-danger",
      accept: () => void performSave(),
    })
  } else {
    void performSave()
  }
}

const performSave = () => {
  const nameUpdates = nameChanges.value
    .filter(
      (c) =>
        acceptedNameUpdates.value.has(c.externalId) &&
        selectedIds.value.has(c.externalId),
    )
    .map((c) => ({ externalId: c.externalId, newName: c.newName }))

  executeSave(props.order.gid, selectedIds.value, nameUpdates, () => {
    visible.value = false
    emit("success")
  })
}
</script>
